import { useCallback, useRef } from "react";
import { trackEvent } from "src/utils/api/tracker";
import { isReferrerFromNoom } from "src/utils/services/VisitTracker";
import { getNavType, getPaintTimings } from "src/utils/timing";
import { isInApp } from "src/utils/userSegment";
import { useOnce } from "./lifecycle";

export function trackAppInit() {
  trackEvent("OnAppInit", {
    visibilityState: document.visibilityState,
    navigationType: getNavType(),

    // This creates a simple session tracker based on the referrer
    // of the current page view. New session = referrer is not from Noom.
    // Likely has edge cases vs using a cookie, but this is consistent
    // with the prior implementation.
    // Read here: https://github.com/noom/airflow-workflows/blob/master/dags/templates/mixpanel/raw_data.mixpanel_landing_and_plan_page_events.js
    new_session: !isInApp() && !isReferrerFromNoom(),
  });
}

export function trackPageView(eventName: string, extraProps?: JsonObject) {
  trackEvent(eventName, {
    ...getPaintTimings(),
    ...extraProps,
  });
}

export function usePageView(eventName: string, extraProps?: JsonObject) {
  useOnce(() => {
    trackPageView(eventName, extraProps);
  });
}

/**
 * Tracks the time to complete a task. Emits event if not completed
 * within the timeout.
 */
export function useLongRunningTracker(
  eventName: string,
  extraProps?: JsonObject,
  timeout = 10_000
) {
  const ref = useRef<ReturnType<typeof setTimeout>>();

  useOnce(() => {
    ref.current = setTimeout(() => {
      trackEvent(eventName, {
        ...getPaintTimings(),
        ...extraProps,
      });
    }, timeout);

    return () => {
      clearTimeout(ref.current);
    };
  });

  return {
    taskComplete: useCallback(() => {
      clearTimeout(ref.current);
    }, []),
  };
}
